import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, ExternalLink, Eraser, Loader2, Save } from "lucide-react";
import { SITE_DEFAULTS, WEB_PRIVACY_PATH, WEB_TERMS_PATH, legalIdentityComplete, siteLegalSchema, type SiteLegal } from "@wedding/shared";
import { adminSiteApi } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Yasal" (F9): veri sorumlusu / hizmet sağlayıcı kimliği.
 * KVKK aydınlatma metni (`/privacy`) ve Kullanım koşulları (`/terms`)
 * buradan beslenir; boş alan sayfada hiç çizilmez. Varsayılan BOŞ —
 * kişisel bilgiler kullanıcı kararıyla askıda (2026-10-13).
 */

const WEB_BASE =
  (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

type Field = keyof SiteLegal;

const FIELDS: { key: Field; labelKey: string; multiline?: boolean; type?: string; autoComplete?: string }[] = [
  { key: "name", labelKey: "siteAdmin.legal.name", autoComplete: "organization" },
  { key: "title", labelKey: "siteAdmin.legal.titleField", autoComplete: "organization-title" },
  { key: "address", labelKey: "siteAdmin.legal.address", multiline: true, autoComplete: "street-address" },
  { key: "phone", labelKey: "siteAdmin.legal.phone", type: "tel", autoComplete: "tel" },
  { key: "email", labelKey: "siteAdmin.legal.email", type: "email", autoComplete: "email" },
  { key: "kep", labelKey: "siteAdmin.legal.kep", type: "email" },
  { key: "taxOffice", labelKey: "siteAdmin.legal.taxOffice" },
  { key: "taxId", labelKey: "siteAdmin.legal.taxId", type: "text" },
  { key: "mersis", labelKey: "siteAdmin.legal.mersis" },
];

export function LegalTab({ initial, onSaved, showToast }: {
  initial: SiteLegal;
  onSaved: (l: SiteLegal) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [base, setBase] = useState(initial);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  const parsed = siteLegalSchema.safeParse(draft);
  const fieldErrors = new Set(parsed.success ? [] : parsed.error.issues.map((i) => String(i.path[0])));
  const dirty = (Object.keys(draft) as Field[]).some((k) => draft[k] !== base[k]);
  const complete = legalIdentityComplete(parsed.success ? parsed.data : draft);

  const persist = async (value: SiteLegal) => {
    setSaving(true);
    try {
      const res = await adminSiteApi.put("legal", value);
      const saved = res.data ?? value;
      setBase(saved);
      setDraft(saved);
      onSaved(saved);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!parsed.success) {
      showToast(t("siteAdmin.invalid"), false);
      return;
    }
    void persist(parsed.data);
  };

  const clearAll = () => {
    if (!window.confirm(t("siteAdmin.legal.clearConfirm"))) return;
    void persist(SITE_DEFAULTS.legal);
  };

  const input =
    "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 aria-[invalid=true]:border-red-500";

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-muted/30">
        <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.legal.title")}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.legal.desc")}</p>
      </div>

      <div className="space-y-5 px-5 py-4">
        <p className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
          {t("siteAdmin.legal.publicWarn")}
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => {
            const id = `legal-${f.key}`;
            const invalid = fieldErrors.has(f.key) || undefined;
            return (
              <div key={f.key} className={cn("space-y-1.5", f.multiline && "sm:col-span-2")}>
                <label htmlFor={id} className="text-xs font-medium text-foreground">
                  {t(f.labelKey)}
                </label>
                {f.multiline ? (
                  <textarea
                    id={id}
                    rows={2}
                    value={draft[f.key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                    autoComplete={f.autoComplete}
                    aria-invalid={invalid}
                    className={cn(input, "resize-y")}
                  />
                ) : (
                  <input
                    id={id}
                    type={f.type ?? "text"}
                    value={draft[f.key]}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                    autoComplete={f.autoComplete}
                    aria-invalid={invalid}
                    className={input}
                  />
                )}
              </div>
            );
          })}
        </div>

        <p
          data-testid="legal-status"
          className={cn(
            "flex items-start gap-2 text-sm",
            complete ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground",
          )}
        >
          {complete ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" /> : null}
          {complete ? t("siteAdmin.legal.complete") : t("siteAdmin.legal.incomplete")}
        </p>

        <div className="flex flex-wrap gap-2 text-sm">
          {[
            { href: `${WEB_BASE}${WEB_PRIVACY_PATH}`, label: t("siteAdmin.legal.openKvkk") },
            { href: `${WEB_BASE}${WEB_TERMS_PATH}`, label: t("siteAdmin.legal.openTerms") },
          ].map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {l.label}
            </a>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2 px-5 pb-4">
        <button
          type="button"
          onClick={clearAll}
          disabled={saving || Object.values(base).every((v) => v === "")}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
          {t("siteAdmin.legal.clear")}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? t("siteAdmin.saving") : t("siteAdmin.save")}
        </button>
      </div>
    </div>
  );
}
